import { BrandKit } from "@redotech/redo-model/brand-kit";
import {
  DiscountElement,
  LogoElement,
  MediaUploadData,
  MediaUploadElement,
  ParagraphData,
  ParagraphElement,
  ProductDetailsElement,
  ReviewDescriptionSemanticTag,
  ReviewEmailSemanticTag,
  ReviewForgeElementType,
  ReviewForgeSemanticTag,
  ReviewForgeSubmittableElement,
  ReviewMediaSemanticTag,
  ReviewRatingSemanticTag,
  ReviewRecommendedSemanticTag,
  ReviewTitleSemanticTag,
  RichTextElement,
  ShortAnswerData,
  ShortAnswerElement,
  SingleSelectData,
  SingleSelectElement,
  StarsData,
  StarsElement,
  SubmittableElementData,
  SystemReviewSemanticTags,
  SystemSemanticTagId,
  ThankYouUpsellElementType,
} from "@redotech/redo-model/forge/review-forge-types";
import { groupInput, input, InputProvider, listInput } from "@redotech/ui/form";
import { isEnumValue } from "@redotech/util/enum";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever, sinkValue } from "@redotech/util/type";
import lodashDeepIsEqual from "lodash/isEqual";
import { WidgetEditorProps } from "../form-builder/widget-editors/widget-editor-decorator";

let uniqueNumberGenerator = 1;

// and all the interactive elements that take customer input
export type WriteableElement =
  | ShortAnswerElement
  | StarsElement
  | MediaUploadElement
  | SingleSelectElement<number>
  | ParagraphElement;

// all the static-only elements
export type StaticElement =
  | RichTextElement
  | ProductDetailsElement
  | LogoElement;

export type PossiblePageElement =
  | StaticElement
  | WriteableElement
  | DiscountElement;

export type PossibleThankYouPageElement =
  | StaticElement
  | DiscountElement; /* | UpsellElement
   | StartAnotherReviewElement; */

export type AllReviewFormElements =
  | PossiblePageElement
  | PossibleThankYouPageElement;

// This will be useful when we are creating new submittable semantic tags
type OptionalTag<T extends ReviewForgeSubmittableElement<any>> = Omit<
  T,
  "tag"
> & { tag: ReviewForgeSemanticTag<any> | null };

const temp: Partial<OptionalTag<ShortAnswerElement>> = {};
sinkValue(temp);

export type PossibleSubmittableData =
  | ShortAnswerData
  | StarsData
  | MediaUploadData
  | SingleSelectData
  | ParagraphData;

export type ReviewElementProps<T extends PossiblePageElement> = { element: T };

export type WriteableReviewElementProps<
  T extends WriteableElement,
  D extends SubmittableElementData<any>,
> = { element: T; data?: D; setData: (data: D) => void; showRequired: boolean };

export interface ReviewElementEditorProps<T extends PossiblePageElement>
  extends WidgetEditorProps<ReviewElementProps<T>, T> {
  widget: ReviewElementProps<T>;
}

export interface WriteableReviewElementEditorProps<
  T extends WriteableElement,
  D extends SubmittableElementData<any>,
> extends WidgetEditorProps<WriteableReviewElementProps<T, D>, T> {
  widget: WriteableReviewElementProps<T, D>;
}

export interface Page {
  elements: PossiblePageElement[];
}

export enum ReviewsFormGlobalTabs {
  OVERVIEW = "Overview",
  STYLES = "Styles",
}

const pageInput = groupInput({
  elements: input<PossiblePageElement[]>({ equal: lodashDeepIsEqual }),
});

const pageInputDefault = (): PageValue => {
  return { elements: [] };
};

const thankYouPageInput = groupInput({
  elements: input<PossibleThankYouPageElement[]>({ equal: lodashDeepIsEqual }),
});

const defaultThankYouPage = (brandKit: BrandKit): ThankYouPageValue => ({
  elements: [
    defaultLogo(),
    getRichText(brandKit, {
      html: '<h1 style="text-align: center;">Thank you</h1>',
    }),
  ],
});

const pagesInput = listInput(
  () => pageInput,
  pageInputDefault,
  (page, index) => index,
);

export const reviewsForm = groupInput({
  settings: groupInput({
    title: input<string>(),
    description: input<string>(),
    categories: input<string[]>(),
  }),
  styles: groupInput({
    brandKitOverride: input<BrandKit>(),
    brandKitEnabled: input<boolean>(),
  }),
  pages: pagesInput,
  thankYouPage: thankYouPageInput,
});

export type PageValue = InputProvider.Value<typeof pageInput>;
export type PageForm = InputProvider.Form<typeof pageInput>;
export type ThankYouPageForm = InputProvider.Form<typeof thankYouPageInput>;
export type ThankYouPageValue = InputProvider.Value<typeof thankYouPageInput>;
export type PagesForm = InputProvider.Form<typeof pagesInput>;

export type ReviewsFormValue = InputProvider.Value<typeof reviewsForm>;
export type ReviewsFormForm = InputProvider.Form<typeof reviewsForm>;

const defaultProductDetails = (): ProductDetailsElement => {
  return {
    type: ReviewForgeElementType.PRODUCT_DETAILS,
    imageWidthPercentage: 50,
  };
};

export const ReviewForgeDroppableItems = [
  ...ThankYouUpsellElementType,
  ...SystemReviewSemanticTags.map((tag) => tag.tagId),
];
export type ReviewForgeDroppableItem =
  (typeof ReviewForgeDroppableItems)[number];

const defaultLogo = (): LogoElement => ({
  type: ReviewForgeElementType.LOGO,
  widthPercentage: 30,
});

export function getReviewsFormDefault(brandKit: BrandKit): ReviewsFormValue {
  const brandKitOverride = deepCopyObject(brandKit);
  brandKitOverride.images.logoUrl =
    "https://assets.getredo.com/generic-logo.png";

  return {
    settings: {
      title: "Review form",
      description: "This form is sent to customers to collect reviews",
      categories: [],
    },
    styles: { brandKitOverride, brandKitEnabled: false },
    pages: [
      {
        elements: [
          defaultLogo(),
          defaultProductDetails(),
          reviewRatingElement(),
          reviewTitleElement(),
          reviewDescriptionElement(),
          reviewEmailElement(),
        ],
      },
    ],
    thankYouPage: defaultThankYouPage(brandKit),
  };
}

export const getRichText = (
  brandKit: BrandKit,
  overrides: Partial<RichTextElement> = {},
): RichTextElement => {
  const fontFamily = brandKit.font.bodyFontFamily; // NOTE: Assuming body font is used for rich text
  const fontSize = brandKit.font.hierarchy.h3.fontSizePx;
  const fontWeight = brandKit.font.hierarchy.h3.fontWeight;

  const defaultValue: RichTextElement = {
    id: uniqueNumberGenerator++,
    type: ReviewForgeElementType.RICH_TEXT,
    html: "<p></p>",
    fontFamily,
    fontWeight,
    fontSizePx: fontSize,
    textColor: "#000000",
    linkColor: "#0000ff",
  };

  return { ...defaultValue, ...overrides };
};

export const defaultDiscount = (): DiscountElement => {
  return { type: ReviewForgeElementType.DISCOUNT, discountId: undefined };
};

const reviewMedia = (): MediaUploadElement => {
  return {
    type: ReviewForgeElementType.MEDIA_UPLOAD,
    required: false,
    widthPercentage: 50,
    tag: ReviewMediaSemanticTag,
  };
};

const reviewRecommended = (): SingleSelectElement<2> => {
  return {
    type: ReviewForgeElementType.SINGLE_SELECT,
    options: [
      "Yes, I would recommend this product",
      "No, I would not recommend this product",
    ],
    required: true,
    tag: ReviewRecommendedSemanticTag,
  };
};

const reviewEmailElement = (): ShortAnswerElement => {
  return {
    type: ReviewForgeElementType.SHORT_ANSWER,
    required: false,
    title: "Email",
    placeholderText: "Enter your email address",
    tag: ReviewEmailSemanticTag,
  };
};

const reviewTitleElement = (): ShortAnswerElement => {
  return {
    type: ReviewForgeElementType.SHORT_ANSWER,
    required: true,
    title: "Title of your review",
    placeholderText: "",
    tag: ReviewTitleSemanticTag,
  };
};

const reviewDescriptionElement = (): ParagraphElement => {
  return {
    type: ReviewForgeElementType.PARAGRAPH,
    title: "Comments",
    placeholderText:
      "Let shoppers know what you liked or didn’t like about this purchase",
    required: true,
    tag: ReviewDescriptionSemanticTag,
  };
};

const reviewRatingElement = (): StarsElement => {
  return {
    type: ReviewForgeElementType.STARS,
    title: "Review the product",
    required: true,
    tag: ReviewRatingSemanticTag,
  };
};

export function getSemanticTagElement(id: string): WriteableElement {
  const tag = SystemReviewSemanticTags.find((tag) => tag.tagId === id);
  if (!tag) {
    throw new Error(`Unknown semantic tag id: ${id}`);
  }

  const tagId = tag.tagId;
  if (!isEnumValue(tagId, SystemSemanticTagId)) {
    throw new Error(`Unknown semantic tag id: ${tagId}`);
  }

  switch (tagId) {
    case SystemSemanticTagId.REVIEW_RATING:
      return reviewRatingElement();
    case SystemSemanticTagId.REVIEW_TITLE:
      return reviewTitleElement();
    case SystemSemanticTagId.REVIEW_DESCRIPTION:
      return reviewDescriptionElement();
    case SystemSemanticTagId.REVIEW_MEDIA:
      return reviewMedia();
    case SystemSemanticTagId.REVIEW_RECOMMENDED:
      return reviewRecommended();
    case SystemSemanticTagId.REVIEW_EMAIL:
      return reviewEmailElement();
    default:
      assertNever(tagId);
  }
}

export function getDefaultElement(
  type: ThankYouUpsellElementType,
  brandKit: BrandKit,
): PossibleThankYouPageElement {
  switch (type) {
    case ReviewForgeElementType.RICH_TEXT:
      return getRichText(brandKit);
    case ReviewForgeElementType.PRODUCT_DETAILS:
      return defaultProductDetails();
    case ReviewForgeElementType.LOGO:
      return defaultLogo();
    case ReviewForgeElementType.DISCOUNT:
      return defaultDiscount();
    default:
      assertNever(type);
  }
}

export function elementIsWriteable(
  element: AllReviewFormElements,
): element is WriteableElement {
  return (element as WriteableElement).tag !== undefined;
}
